import styled from 'styled-components';
import { toRem, baseVariables } from '@marriott/mi-ui-library';
import rtl from 'styled-components-rtl';

/**
 * V2 Style Constants
 */
const v2StyleConstants = {
  bgCardInfo: '#f4f4f4',
  bgCardDetails: 'white',
  spacerSection: 24,
  spacerSectionMd: 32,
  spacerSectionLg: 48,
  spacerGap: 8,
  spacerGapLarge: 24,
  borderRadiusMobile: 14,
  borderRadiusDesktop: 16,
  shadow: '0px 4px 20px rgba(0, 0, 0, 0.07)',
};

/**
 * Styles for CardDetails organism
 * Leverages global Bootstrap utilities (d-flex, align-items-center, mt-*, etc.)
 * Only custom styles that aren't available in global classes are defined here
 */
export const StyledCardDetails = styled.div`
  ${rtl`
    /* Custom border and shadow for card container */
    .card-detail--border {
      border: ${baseVariables.border.borderWidth02} ${baseVariables.border.borderStyleDefault} ${
    baseVariables.color.neutral20
  };
      border-radius: ${toRem(16)};
      box-shadow: 0 ${toRem(4)} ${toRem(20)} ${baseVariables.shadows.boxShadowTile};
      margin-top: ${toRem(72)};

      @media ${baseVariables.mediaQuery.md} {
        margin-top: 0;
      }
    }

    /* Custom link display behavior */
    .card-detail-wrapper .custom-link {
      display: block;

      @media ${baseVariables.mediaQuery.lg} {
        display: inline;
      }
    }

    .card-detail-wrapper .points-underlined {
       font-family: ${baseVariables.font.fontSwissCondensed};
        border-bottom: ${toRem(5)} ${baseVariables.border.borderStyleDefault} ${baseVariables.color.accent10};
        display: block;
        width: fit-content;
        margin: ${toRem(4)} auto ${toRem(8)} auto;
        @media ${baseVariables.mediaQuery.md} {
          margin: ${toRem(8)} auto ${toRem(12)} auto;
        }
        @media ${baseVariables.mediaQuery.lg} {
          margin: 0;
          display: inline;
        }
      }

    /* Accordion button position override */
    .card-detail-wrapper .accordion-button {
      position: static;
    }

    /* Responsive vertical alignment utilities */
    .middle-align {
      vertical-align: super;

      @media ${baseVariables.mediaQuery.md} {
        vertical-align: unset;
      }

      @media ${baseVariables.mediaQuery.lg} {
        vertical-align: super;
      }
    }

    /* Responsive flex/block toggle - can't be achieved with single Bootstrap class */
    .vertical-align {
      display: flex;
      align-items: center;
      justify-content: center;

      @media ${baseVariables.mediaQuery.md} {
        display: block;
      }

      @media ${baseVariables.mediaQuery.lg} {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }

    /* Responsive top border - Desktop only */
    .top-horizontal-border {
      border: none;

      @media ${baseVariables.mediaQuery.md} {
        padding-top: ${toRem(16)};
        display: block;
        border-top: ${baseVariables.border.borderWidth02} ${baseVariables.border.borderStyleDefault} ${
    baseVariables.color.neutral30
  };
      }
    }

    /* RTE content spacing - uses :has() for parent-child selector */
    p:has(> span.t-subtitle-m) {
      margin-bottom: ${toRem(8)};
    }

    p:has(> span.t-numbers-s) {
      margin-bottom: ${toRem(16)};
    }

    .flex-align-center {
      display: flex;
      align-items: center;
    }

    /* ===== V2 Layout Styles ===== */
    
    /* V2 parent container - add vertical spacing between cards */
    &.card-details--v2 {
      margin: ${toRem(40)} 0 !important;
      padding: 0;
      
      @media ${baseVariables.mediaQuery.md} {
        margin: ${toRem(48)} 0 !important;
      }
      
      @media ${baseVariables.mediaQuery.lg} {
        margin: ${toRem(64)} 0 !important;
      }
    }
    
    /* V2 Two-section wrapper */
    .card-details-v2-wrapper {
      display: flex;
      flex-direction: column;
      border-radius: ${toRem(v2StyleConstants.borderRadiusMobile)};
      box-shadow: ${v2StyleConstants.shadow};
      overflow: hidden;
      
      @media ${baseVariables.mediaQuery.md} {
        flex-direction: row;
        border-radius: ${toRem(v2StyleConstants.borderRadiusDesktop)};
      }
    }
    
    /* Grey section: card, title, fee, CTA */
    .card-details-v2-card-info {
      background-color: ${v2StyleConstants.bgCardInfo};
      padding: ${toRem(v2StyleConstants.spacerSection)};
      display: flex;
      flex-direction: column;
      align-items: center;
      
      @media ${baseVariables.mediaQuery.md} {
        padding: ${toRem(v2StyleConstants.spacerSectionMd)};
        flex: 0 0 40%;
        max-width: ${toRem(300)};
      }
      
      @media ${baseVariables.mediaQuery.lg} {
        padding: ${toRem(v2StyleConstants.spacerSectionLg)};
        flex: 0 0 35%;
      }
      
      /* Card image sizing in V2 */
      .sticky-card-image {
        width: ${toRem(178)};
        height: auto;
        
        @media ${baseVariables.mediaQuery.md} {
          max-width: ${toRem(240)};
        }
      }
    }
    
    /* White section: offer, benefits, accordion */
    .card-details-v2-card-details {
      background-color: ${v2StyleConstants.bgCardDetails};
      padding: ${toRem(v2StyleConstants.spacerSection)};
      display: flex;
      flex-direction: column;
      
      @media ${baseVariables.mediaQuery.md} {
        padding: ${toRem(v2StyleConstants.spacerSectionMd)};
        flex: 1 1 60%;
      }
      
      @media ${baseVariables.mediaQuery.lg} {
        padding: ${toRem(v2StyleConstants.spacerSectionLg)};
        flex: 1 1 65%;
      }
      .points-card-detail{
        flex-direction: column !important;
        .points{
          font-family: ${baseVariables.font.fontSwiss};
          font-size:  ${toRem(16)};
          color: ${baseVariables.color.alert40};
          min-width: unset;
          ul{
           padding-left: ${toRem(20)};
          }
        }
        .points-description {
          margin-left:  ${toRem(24)} !important;
          .t-subtitle-m{
           font-weight: 500;
          }
        }
      }
    }
    
    /* Annual fee section */
    .card-details-annual-fee {
      display: flex;
      flex-direction: column;
      gap: ${toRem(v2StyleConstants.spacerGap)};
      align-items: center;
      text-align: center;
      width: 100%;
    }
    
    /* Featured offer highlight color */
    .featured-offer-highlight {
      color: ${baseVariables.color.alert40};
    }

    /* V2 Accordion styles - works on all breakpoints */
    .card-details-v2-accordion {
      margin-top: ${toRem(v2StyleConstants.spacerGapLarge)};

    .accordion-collapse{
      ul{
       padding-left: ${toRem(20)};
       li {
        margin-bottom: ${toRem(8)};
       }
       li:last-child {
        margin-bottom: ${toRem(24)};
       }
      }
      li::marker{
       color: ${baseVariables.color.alert40};
      }
    }
  }
    .links-section {
     margin-top: ${toRem(8)};
      p{
       margin-bottom: ${toRem(24)};
      }
      p:last-child{
       margin-bottom: ${toRem(0)};
      }
    }
    a {
      font-weight: 500;
      text-underline-offset: ${toRem(3)};
    }
    .nerd-wallet-image{
     width: ${toRem(176)};
     height: ${toRem(49)};
     margin:  ${toRem(24)} auto;
     display: flex;
    }
 `}
`;
